<template>
    <div v-if="visible" class="recent-authentication" data-test-id="recent-authentication-modal">
        <div
            ref="dialog"
            class="modal d-block"
            tabindex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="recent-authentication-title"
            aria-describedby="recent-authentication-instructions">
            <div class="modal-dialog modal-dialog-centered" role="document">
                <form class="modal-content" @submit.prevent="confirm">
                    <div class="modal-header">
                        <h5 id="recent-authentication-title" class="modal-title">
                            {{ $tl("labels.form.confirmPassword.title") }}
                        </h5>
                    </div>
                    <div class="modal-body">
                        <div v-if="errorMessage" id="recent-authentication-error" class="alert alert-danger" role="alert" aria-live="polite">
                            {{ errorMessage }}
                        </div>
                        <p id="recent-authentication-instructions" class="text-muted">
                            {{ $tl(instructionsKey) }}
                        </p>
                        <template v-if="!requiresFreshLogin">
                            <label class="form-label" for="recent-authentication-password">{{ $tl("labels.form.confirmPassword.password") }}</label>
                            <input
                                id="recent-authentication-password"
                                ref="passwordInput"
                                v-model="password"
                                type="password"
                                class="form-control"
                                :class="{ 'is-invalid': errorMessage }"
                                :aria-invalid="errorMessage ? 'true' : 'false'"
                                :aria-describedby="errorMessage ? 'recent-authentication-error' : undefined"
                                autocomplete="current-password"
                                required
                                @input="errorMessage = null" />
                        </template>
                    </div>
                    <div class="modal-footer recent-authentication__actions">
                        <button type="button" class="btn btn-outline-secondary" :disabled="submitting || loggingOut" @click="cancel">
                            {{ $tl("labels.form.confirmPassword.cancel") }}
                        </button>
                        <button v-if="!requiresFreshLogin" type="button" class="btn btn-link" :disabled="submitting || loggingOut" @click="logout">
                            {{ $tl("labels.form.mfa.useAnotherAccount") }}
                        </button>
                        <button ref="confirmButton" type="submit" class="btn btn-success" :disabled="submitting || loggingOut">
                            {{ $tl(confirmKey) }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
        <div class="modal-backdrop show"></div>
    </div>
</template>

<script>
import axios from "axios";
import { cancelPendingRequests, retryPendingRequests, subscribeToRecentAuthentication } from "../../axios/RecentAuthenticationRetry.mjs";

export default {
    name: "RecentAuthenticationModal",
    data() {
        return {
            visible: false,
            password: "",
            errorMessage: null,
            submitting: false,
            loggingOut: false,
            unsubscribe: null,
            previouslyFocusedElement: null,
            backgroundElements: [],
            reauthenticationReason: null,
        };
    },
    computed: {
        requiresFreshLogin() {
            return ["external_reauthentication_required", "admin_reauthentication_required"].includes(this.reauthenticationReason);
        },
        instructionsKey() {
            if (this.reauthenticationReason === "admin_reauthentication_required") {
                return "labels.form.confirmPassword.adminInstructions";
            }

            return this.requiresFreshLogin ? "labels.form.confirmPassword.externalInstructions" : "labels.form.confirmPassword.instructions";
        },
        confirmKey() {
            if (this.reauthenticationReason === "admin_reauthentication_required") {
                return "labels.form.confirmPassword.adminConfirm";
            }

            return this.requiresFreshLogin ? "labels.form.confirmPassword.externalConfirm" : "labels.form.confirmPassword.confirm";
        },
    },
    created() {
        this.unsubscribe = subscribeToRecentAuthentication(({ visible, challenge }) => {
            const isOpening = visible && !this.visible;

            if (!visible) {
                this.restoreBackground();
            } else if (isOpening) {
                this.previouslyFocusedElement = document.activeElement;
            }

            this.visible = visible;
            this.reauthenticationReason = challenge?.reason || null;

            if (isOpening) {
                this.$nextTick(() => {
                    this.hideBackground();
                    (this.$refs.passwordInput || this.$refs.confirmButton)?.focus?.();
                });
            } else if (!visible) {
                this.$nextTick(() => {
                    this.previouslyFocusedElement?.focus?.();
                    this.previouslyFocusedElement = null;
                });
            }
        });

        document.addEventListener("keydown", this.handleKeydown);
    },
    beforeDestroy() {
        this.unsubscribe?.();
        document.removeEventListener("keydown", this.handleKeydown);
        this.restoreBackground();
    },
    methods: {
        hideBackground() {
            const parent = this.$el?.parentElement;

            if (!parent) {
                return;
            }

            this.backgroundElements = Array.from(parent.children)
                .filter((element) => element !== this.$el)
                .map((element) => ({
                    element,
                    ariaHidden: element.getAttribute("aria-hidden"),
                    inert: element.inert,
                }));

            this.backgroundElements.forEach(({ element }) => {
                element.inert = true;
                element.setAttribute("aria-hidden", "true");
            });
        },
        restoreBackground() {
            this.backgroundElements.forEach(({ element, ariaHidden, inert }) => {
                element.inert = inert;

                if (ariaHidden === null) {
                    element.removeAttribute("aria-hidden");
                } else {
                    element.setAttribute("aria-hidden", ariaHidden);
                }
            });
            this.backgroundElements = [];
        },
        handleKeydown(event) {
            if (!this.visible) {
                return;
            }

            if (event.key === "Escape" && !this.submitting && !this.loggingOut) {
                event.preventDefault();
                this.cancel();
                return;
            }

            if (event.key !== "Tab") {
                return;
            }

            const focusableElements = Array.from(
                this.$refs.dialog?.querySelectorAll(
                    'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
                ) || []
            );
            const firstElement = focusableElements[0];
            const lastElement = focusableElements.at(-1);

            if (!firstElement || !lastElement) {
                event.preventDefault();
                this.$refs.dialog?.focus();
                return;
            }

            if (event.shiftKey && (document.activeElement === firstElement || !this.$refs.dialog.contains(document.activeElement))) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && (document.activeElement === lastElement || !this.$refs.dialog.contains(document.activeElement))) {
                event.preventDefault();
                firstElement.focus();
            }
        },
        cancel() {
            this.password = "";
            this.errorMessage = null;
            cancelPendingRequests(new axios.Cancel("Password confirmation was cancelled."));
        },
        confirm() {
            if (this.requiresFreshLogin) {
                this.logout();
                return;
            }

            this.submitting = true;
            this.errorMessage = null;

            axios
                .post("/password/confirm", { password: this.password })
                .then(() => {
                    this.password = "";
                    retryPendingRequests((config) => axios.request(config));
                })
                .catch((error) => {
                    this.errorMessage = error.response?.data?.message || this.$tl("labels.form.confirmPassword.invalid");
                    this.password = "";
                    this.$nextTick(() => this.$refs.passwordInput?.focus());
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
        logout() {
            this.loggingOut = true;

            axios
                .post("/logout")
                .then((response) => {
                    cancelPendingRequests(new axios.Cancel("The user selected another account."));
                    window.location.replace(response.data.redirect_url || response.headers.location || "/login");
                })
                .catch((error) => {
                    this.errorMessage = error.response?.data?.message || this.$tl("labels.form.confirmPassword.logoutError");
                })
                .finally(() => {
                    this.loggingOut = false;
                });
        },
    },
};
</script>

<style scoped>
.recent-authentication .modal {
    z-index: 1060;
}

.recent-authentication .modal-backdrop {
    z-index: 1050;
}

.recent-authentication__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 0.5rem;
}
</style>
